import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { BEIGE_400, BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, Stack, TableCell, TableRow, Typography, styled } from '@mui/material'

import { FC } from 'react'
import { MUIButton } from 'components/common/MUIButton'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Path } from 'constants/router'
import PropertyPlaceholderIcon from 'assets/img/icons/property_placeholder.svg?react'
import { RealEstatePropertyListItemDTO } from 'models/property'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { externalNavigate } from 'utils/helpers'
import moment from 'moment'
import { useCanBulkActOnProperties } from '../contexts'
import { usePropertiesDashboard } from '../contexts/PropertiesDashboard.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const StyledTableRow = styled(TableRow)(() => ({
  '&.MuiTableRow-hover': {
    ':hover': {
      backgroundColor: BEIGE_400,
    }
  },
}))

/**
 * @interface Props
 */
interface Props {
  /** Property data. */
  property: RealEstatePropertyListItemDTO
}

/**
 * @component Properties dashboard table row
 * @example
 * <PropertiesDashboardTableRow />
 */
export const PropertiesDashboardTableRow: FC<Props> = ({ property }) => {
  const { t } = useTranslation(['properties_dashboard'])
  const { currentUserWorkspace } = useUserData()
  const { isSelected, toggleSelected, isSelectionFull } = usePropertiesDashboard()
  const showCheckbox = useCanBulkActOnProperties()

  const onOpenDetailClick = () => {
    logAnalyticsEvent(AnalyticsEvent.OPEN_PROPERTY, {
      realtyId: property.id,
      propertyId: property.id,
      propertyName: property.title,
      planId: currentUserWorkspace?.id,
    })

    externalNavigate(Path.PROPERTY_VISUALS_GALLERY.replace(':id', property.id))
  }

  return (
    <StyledTableRow
      hover
      key={property.id}
      onClick={onOpenDetailClick}
      sx={{
        cursor: 'pointer',
        ':hover .property-title': {
          textDecorationLine: 'underline',
        }
      }}
    >

      {showCheckbox &&
        <TableCell onClick={(e) => e.stopPropagation()}>
          <MUICheckbox
            checked={isSelected(property.id)}
            disabled={!isSelected(property.id) && isSelectionFull}
            onClick={(e) => {
              e.stopPropagation()
              toggleSelected(property.id)
            }}
          />
        </TableCell>
      }

      {/* Title */}
      <TableCell component="th" scope="row">
        <Stack flexDirection="row" alignItems="center" gap="1.6rem">

          <Box borderRadius=".4rem" padding=".6rem .6rem .4rem .6rem" sx={{ background: BEIGE_600 }}>
            <PropertyPlaceholderIcon />
          </Box>

          <Typography className="property-title" variant="text-sm" fontWeight="semiBold" color={GRAY_900} sx={{}}>{property.title ? property.title : '-'}</Typography>

        </Stack>
      </TableCell>

      {/* Address */}
      <TableCell scope="row">
        {!property.address
          ? <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>{'-'}</Typography>

          : <Stack>

            <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>{property.address?.postalCode} {property.address?.street} {property.address?.streetNumber}</Typography>

            <Typography
              variant="text-xs"
              color={GRAY_700}
              fontWeight={400}
              sx={{
              }}
            >
              {property.address?.city} {property.address?.country && ','} {property.address?.country}

            </Typography>

          </Stack>
        }
      </TableCell>

      {/* Created date */}
      <TableCell>
        <Typography variant="text-sm" fontWeight={400} color={GRAY_900}>{moment(property.createdOn).format('DD/MM/YYYY')}</Typography>
      </TableCell>

      {/* Last updated date */}
      <TableCell>
        <Typography variant="text-sm" fontWeight={400} color={GRAY_900}>{moment(property.updatedAt).format('DD/MM/YYYY')}</Typography>
      </TableCell>

      {/* Property manager */}
      <TableCell>
        <Typography variant="text-sm" fontWeight={400} color={GRAY_900}>{property.broker?.name ? property.broker.name : '-'}</Typography>
      </TableCell>

      {/* Action */}
      <TableCell
        align="right"
        sx={{
          position: 'relative',
        }}
      >
        <MUIDropdown
          button={(isOpen, action) => (
            <MUIButton
              isIconButton
              size="sm"
              type="secondaryNoBorder"
              onClick={(e) => action(e)}
            >
              <MoreVertIcon fontSize="large" />
            </MUIButton>
          )}
        >
          <MUIDropdownItem onClick={onOpenDetailClick}>
            <Stack flexDirection="row" alignItems="center" gap=".8rem">

              <VisibilityOutlinedIcon />

              <Typography variant="text-md" color={GRAY_900} fontWeight={500}>
                {t('show')}
              </Typography>

            </Stack>
          </MUIDropdownItem>
        </MUIDropdown>
      </TableCell>

    </StyledTableRow>
  )
}
